import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Text, Tooltip } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreBooking } from '@flecto/types'
import {
  AvatarProductsGroup,
  Badge,
  BadgeBookingPaymentDepositStatus,
  BadgeBookingPaymentRentalStatus,
  BadgeBookingStatus,
  Icon,
  TextCurrency,
  TextDate,
  type DataTableColumn,
} from '@flecto/ui'

import { BadgeBookingInsuranceStatus } from '../BadgeBookingInsuranceStatus/BadgeBookingInsuranceStatus'

export const useColumns = () => {
  const { t, i18n } = useTranslation('saas', { keyPrefix: 'bookings' })

  const columns: DataTableColumn<CoreBooking>[] = [
    {
      accessor: 'id',
      title: 'ID',
      width: 60,
      render: (row) => <Text c='dimmed'>{row.internal_id}</Text>,
      sortable: true,
    },
    {
      accessor: 'customer_name',
      title: t('label_customer'),
      width: 200,
      sortable: true,
      render: (row) => (
        <Flex mih={45} align='center'>
          {!row.customer ? (
            <Badge color='gray'>{t('label_no_customer')}</Badge>
          ) : (
            <Flex style={{ overflow: 'hidden' }} direction='column'>
              <Text fw={500} truncate>
                {row.customer?.full_name}
              </Text>
              <Text c='dimmed' size='sm' truncate>
                {row.customer?.email}
              </Text>
              <Text c='dimmed' size='sm' truncate>
                {row.customer?.phone_number}
              </Text>
            </Flex>
          )}
        </Flex>
      ),
    },
    {
      accessor: 'products',
      title: t('label_products'),
      width: 180,
      render: (row) => {
        const products = (row.booking_products ?? []).map((bp) => bp.product)
        return <AvatarProductsGroup products={products} />
      },
    },
    {
      accessor: 'status',
      title: t('label_booking_status'),
      width: 150,
      render: (row) => (
        <BadgeBookingStatus booking={row} key={`${row.id}-${i18n.language}`} />
      ),
    },
    {
      accessor: 'start_at',
      title: t('label_dates'),
      width: 360,
      sortable: true,
      render: (row) => {
        if (!row.start_at && !row.end_at) {
          return <Badge color='gray'>{t('label_no_dates')}</Badge>
        }

        return (
          <Flex align='center' gap='sm'>
            <TextDate date={row.start_at} variant='datetime-brief' />
            <Icon icon={faArrowRight} color='gray.7' />
            <TextDate date={row.end_at} variant='datetime-brief' />
          </Flex>
        )
      },
    },
    {
      accessor: 'meta.rental_and_deposit_total_cents',
      title: t('label_total'),
      width: 140,
      render: (row) => (
        <TextCurrency
          truncate
          cents={row.rental_and_deposit_total_cents ?? 0}
        />
      ),
    },
    {
      accessor: 'meta.items_total_with_vat_cents',
      title: t('label_rental'),
      width: 250,
      render: (row) => (
        <Flex align='center' gap='sm'>
          <TextCurrency truncate cents={row.items_total_with_vat_cents ?? 0} />
          <BadgeBookingPaymentRentalStatus booking={row} />
        </Flex>
      ),
    },
    {
      accessor: 'meta.deposit_cents',
      title: t('label_deposit'),
      width: 250,
      render: (row) => (
        <Flex align='center' gap='sm'>
          <TextCurrency truncate cents={row.deposit_cents ?? 0} />
          <BadgeBookingPaymentDepositStatus booking={row} />
        </Flex>
      ),
    },
    {
      accessor: 'meta.services_total_with_vat_cents',
      title: t('label_services'),
      width: 150,
      render: (row) => (
        <TextCurrency truncate cents={row.services_total_with_vat_cents ?? 0} />
      ),
    },
    {
      accessor: 'notes',
      title: t('label_notes'),
      width: 300,
      render: (row) => {
        const content = row.notes ? row.notes?.replace(/<[^>]*>?/gm, '') : null

        return content ? (
          <Tooltip
            label={
              <Text style={{ whiteSpace: 'break-spaces' }}>{content}</Text>
            }
            maw={300}
          >
            <Text truncate maw={300}>
              {content}
            </Text>
          </Tooltip>
        ) : (
          '-'
        )
      },
    },
    {
      accessor: 'insured',
      title: t('label_insurance'),
      width: 120,
      render: (row) => <BadgeBookingInsuranceStatus booking={row} />,
    },
  ]

  return columns
}
